import styled from "@emotion/styled";
import { APP_BREAKPOINT, APP_CONTENT_WIDTH } from "src/constants/layout";

/**
 * Wrapper that positions the intro card near the top of the chat viewport,
 * accounting for the fixed header on mobile. Mirrors the padding pattern
 * used by MessageListContent so the card lines up with other chat content.
 */
export const IntroCardWrapper = styled.div`
  label: SophieIntroCardWrapper;
  width: min(100%, ${APP_CONTENT_WIDTH}px);
  margin: 0 auto;
  padding: calc(var(--app-top, 0px) + var(--header-bar-height) + 1rem) 1.4rem 0;

  @media (min-width: ${APP_BREAKPOINT}px) {
    padding: 1rem 1.4rem 0;
  }
`;

export const IntroCard = styled.div`
  label: SophieIntroCard;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0.875rem 2.5rem 0.875rem 1rem;
  border-radius: 0.75rem;
  background: rgba(95, 31, 143, 0.12);
  border: 1px solid rgba(95, 31, 143, 0.24);
  color: #5f1f8f;
  font-family: "STK Bureau";
  font-feature-settings:
    "liga" off,
    "clig" off;

  @supports (corner-shape: squircle) {
    corner-shape: squircle;
    border-radius: 1.6rem;
  }
`;

export const IntroCardTitle = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 500;
  line-height: 1.4;
  letter-spacing: -0.28px;
`;

export const IntroCardDescription = styled.p`
  margin: 0;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.4;
  letter-spacing: -0.28px;
`;

export const IntroCardLearnMore = styled.button`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0.25rem 0;
  background: none;
  border: none;
  cursor: pointer;
  color: inherit;
  font: inherit;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.4;
  letter-spacing: -0.28px;
  text-underline-offset: 3px;
  -webkit-tap-highlight-color: transparent;

  svg {
    width: 1rem;
    height: 1rem;
  }

  &:hover {
    opacity: 0.75;
  }

  &:focus-visible {
    outline: 2px solid #5f1f8f;
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

export const IntroCardCloseButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(33, 33, 33, 0.16);
  color: #5f1f8f;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  svg {
    width: 0.8rem;
    height: 0.8rem;

    path {
      stroke: white;
    }
  }

  &:hover {
    background: rgba(95, 31, 143, 0.24);
  }

  &:focus-visible {
    outline: 2px solid #5f1f8f;
    outline-offset: 2px;
  }
`;

/**
 * Bottom sheet contents.
 * The sheet's outer padding is provided by BottomSheet's SheetContent;
 * these styles handle the internal layout of "How your care works".
 */
export const HeroImageWrapper = styled.div`
  position: relative;
  width: calc(100% + 3.2rem);
  margin: -2rem -1.6rem 0rem;

  @media (min-width: 768px) {
    [data-desktop-fullscreen] & {
      margin-top: 0;
      margin-bottom: 2rem;
    }
  }
`;

export const HeroImage = styled.img`
  display: block;
  width: 100%;
  max-width: none;
  height: auto;
  object-fit: cover;

  @media (min-width: 768px) {
    [data-desktop-fullscreen] & {
      border-radius: 0.5rem;
    }
  }
`;

export const HeroImageCurve = styled.svg`
  position: absolute;
  left: 0;
  right: 0;
  bottom: -1px;
  width: 100%;
  height: 30px;
  display: block;
  pointer-events: none;

  @media (min-width: 768px) {
    [data-desktop-fullscreen] & {
      display: none;
    }
  }
`;

export const SheetCloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.24);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  color: #ffffff;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  svg {
    width: 0.85rem;
    height: 0.85rem;

    path {
      stroke: #ffffff;
    }
  }

  &:hover {
    background: rgba(255, 255, 255, 0.36);
  }

  &:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 2px;
  }

  @media (min-width: 768px) {
    [data-desktop-fullscreen] & {
      display: none;
    }
  }
`;

export const DesktopTitleBar = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 3.5rem;
  padding: 0.75rem 3.5rem;
`;

export const DesktopTitle = styled.h2`
  margin: 0;
  font-family: var(--font-family);
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.4;
  color: var(--color-charcoal, #212121);
  text-align: center;
`;

export const DesktopCloseButton = styled.button`
  position: absolute;
  top: 50%;
  right: 1rem;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(33, 33, 33, 0.08);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  svg {
    width: 0.85rem;
    height: 0.85rem;

    path {
      stroke: rgba(33, 33, 33, 0.72);
    }
  }

  &:hover {
    background: rgba(33, 33, 33, 0.14);
  }

  &:focus-visible {
    outline: 2px solid rgba(33, 33, 33, 0.4);
    outline-offset: 2px;
  }
`;

export const SheetTitle = styled.h2`
  margin: 0 0 1rem;
  font-family: var(--font-family-serif);
  font-size: 1.75rem;
  font-weight: 300;
  line-height: 1.2;
  letter-spacing: -0.02em;
  color: var(--color-charcoal, #212121);
  text-align: center;

  @media (min-width: 768px) {
    [data-desktop-fullscreen] & {
      display: none;
    }
  }
`;

export const StepList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

export const StepItem = styled.li`
  position: relative;
  display: grid;
  grid-template-columns: 2.5rem 1fr;
  gap: 0.875rem;
  align-items: start;

  &:not(:last-child)::before {
    content: "";
    position: absolute;
    left: 1.25rem;
    top: 2.5rem;
    bottom: -2.5rem;
    width: 1px;
    background: linear-gradient(
      to bottom,
      rgba(95, 31, 143, 0.24),
      rgba(95, 31, 143, 0.08)
    );
    transform: translateX(-50%);
  }
`;

export const StepIconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: linear-gradient(
    229.45deg,
    #ffdfb4 -17.69%,
    #fddee9 33.52%,
    #c1d7fb 81.64%
  );
  flex-shrink: 0;

  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
`;

export const StepBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding-top: 0.375rem;
`;

export const StepTitle = styled.h3`
  margin: 0;
  font-family: var(--font-family);
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.4;
  letter-spacing: -0.02em;
  color: var(--color-charcoal, #212121);
`;

export const StepDescription = styled.p`
  margin: 0;
  font-family: var(--font-family);
  font-size: 0.9375rem;
  font-weight: 400;
  line-height: 1.4;
  letter-spacing: -0.28px;
  color: var(--color-charcoal-80, rgba(33, 33, 33, 0.8));
`;
